import { Fragment, FunctionComponent, MutableRefObject, ReactElement, ReactNode } from 'react';
import cn from 'classnames';
import {
  Appearance,
  DraftConfiguration,
  DraftDesignConfiguration,
  DraftError,
  PrintArea,
  Rect,
  Size,
  View,
} from '@/types';
import { isColorBlack } from '@/utils/colors/isColorBlack';
import {
  getProductTypeImageUrl,
  getProductTypeOverlayImageUrl,
} from '@/utils/imageUrls/getProductTypeImageUrl';
import { isSmartomat } from '@/utils/params';
import { Origin } from '@/hooks/usePinch';
import { getIsDieCutStickerPrintType } from '@/model/printType/getIsDieCutStickerPrintType';
import { getViewMapByPrintArea } from '@/model/productType/getViewMapByPrintArea';
import { isMacroView } from '@/model/productType/isMacroView';
import { Defs } from './Defs';
import { MacroView } from './MacroView';
import { ConfigurationRenderer } from './configurationRenderer/ConfigurationRenderer';
import { StickerDesignRenderer } from './configurationRenderer/designRenderer/StickerDesignRenderer';

export const getDefaultViewBox = ({ width, height }: Size) => ({
  x: 0,
  y: 0,
  width,
  height,
});

const getScaledViewBox = (viewBox: Rect, scale: number, origin: Origin) => {
  const { x, y, width, height } = viewBox;
  const [originX, originY] = origin;
  const scaledViewWidth = width / scale;
  const scaledViewHeight = height / scale;

  return {
    x: x + (width - scaledViewWidth) * originX,
    y: y + (height - scaledViewHeight) * originY,
    width: scaledViewWidth,
    height: scaledViewHeight,
  };
};

interface ViewerProps {
  children?: ReactNode;
  view: View;
  viewBox?: Rect;
  appearance: Appearance;
  printArea?: PrintArea;
  configurations: DraftConfiguration[];
  className: string;
  productTypeImageSize?: number;
  readOnly?: boolean;
  scale?: number;
  origin?: Origin;
  container?: MutableRefObject<HTMLElement | null>;
  errors?: Record<string, DraftError>;
  selectedConfigurationId?: number;
  onShowEdit?: () => void;
  hideMask?: boolean;
}

export const Viewer: FunctionComponent<ViewerProps> = ({
  view,
  viewBox,
  appearance,
  printArea,
  configurations,
  className,
  productTypeImageSize = 800,
  readOnly = true,
  scale = 1,
  origin,
  container,
  errors = {},
  selectedConfigurationId = 0,
  onShowEdit,
  hideMask,
  children,
}) => {
  const imageParams = {
    width: productTypeImageSize,
    height: productTypeImageSize,
  };
  const appearanceId = appearance.id;
  const productTypeImage = getProductTypeImageUrl(view, appearanceId, imageParams);
  const productTypeOverlayImage = getProductTypeOverlayImageUrl(view, appearanceId, imageParams);

  if (!printArea) {
    return isMacroView(view) ? (
      <svg
        viewBox={`0 0 ${productTypeImageSize} ${productTypeImageSize}`}
        width={productTypeImageSize}
        height={productTypeImageSize}
        className={cn(className, readOnly ? '' : 'draft-viewer__shadow', {
          dark: isColorBlack(appearance.colors[0].value),
        })}
      >
        <MacroView
          imageUrl={productTypeImage}
          width={productTypeImageSize}
          height={productTypeImageSize}
          container={container}
        />
      </svg>
    ) : (
      <img
        src={productTypeImage}
        width={productTypeImageSize}
        height={productTypeImageSize}
        className={cn(className, 'draft-viewer__no-printarea')}
        alt=""
      />
    );
  }

  const viewMap = getViewMapByPrintArea(view, printArea);
  if (!viewBox) viewBox = getDefaultViewBox(viewMap.size);
  const scaledViewBox = scale > 1 && origin ? getScaledViewBox(viewBox, scale, origin) : viewBox;

  const { x, y, width, height } = viewBox;
  const { width: viewWidth, height: viewHeight } = viewMap.size;

  let content: ReactElement;
  if (configurations.length > 0 && getIsDieCutStickerPrintType(configurations[0].printType)) {
    const configuration = configurations[0] as DraftDesignConfiguration;
    content = (
      <StickerDesignRenderer
        key={configuration.cid}
        configuration={configuration}
        appearance={appearance}
        viewMap={viewMap!}
        readOnly={readOnly}
        container={container}
        hasErrors={
          Boolean(errors[configuration.cid]) &&
          Object.values(errors[configuration.cid]).some((error) => Boolean(error))
        }
        isSelected={configuration.cid === selectedConfigurationId}
      />
    );
  } else {
    const configurationIndex = configurations.findIndex(
      (config) => config?.cid === selectedConfigurationId
    );
    const configuration = configurations[configurationIndex] as DraftDesignConfiguration;
    content = (
      <Fragment>
        <Defs
          readOnly={readOnly}
          x={viewWidth}
          y={viewHeight}
          view={view}
          printArea={printArea}
          isPrintAreaSelected={configuration?.cid === selectedConfigurationId}
        />
        <image
          key={productTypeImage}
          xlinkHref={productTypeImage}
          width={viewWidth}
          height={viewHeight}
        />
        {viewMap && printArea && (
          <>
            {!hideMask && (
              <rect
                width={viewMap.size.width}
                height={viewMap.size.height}
                mask="url(#mask)"
                fill="#fff"
                opacity={isSmartomat() ? 0.6 : 0.2}
              />
            )}
            <g transform={`translate(${viewMap.offset.x}, ${viewMap.offset.y})`}>
              {children}
              {configurations.map((configuration) => (
                <ConfigurationRenderer
                  key={configuration.cid}
                  configuration={configuration}
                  viewMap={viewMap}
                  readOnly={readOnly}
                  hasErrors={
                    Boolean(errors[configuration.cid]) &&
                    Object.values(errors[configuration.cid]).some((error) => Boolean(error))
                  }
                  isSelected={configuration.cid === selectedConfigurationId}
                  onShowEdit={onShowEdit}
                />
              ))}
            </g>
          </>
        )}
        {productTypeOverlayImage && (
          <image
            key={productTypeOverlayImage}
            xlinkHref={productTypeOverlayImage}
            x="0"
            y="0"
            width={viewWidth}
            height={viewHeight}
            className="viewer__pt-overlay"
          />
        )}
      </Fragment>
    );
  }

  const { x: scaledX, y: scaledY, width: scaledWidth, height: scaledHeight } = scaledViewBox;

  return (
    <svg
      viewBox={`${scaledX} ${scaledY} ${scaledWidth} ${scaledHeight}`}
      width={width}
      height={height}
      className={cn(className, { dark: isColorBlack(appearance.colors[0].value) })}
    >
      <rect id="visible-area" x={x} y={y} width={width} height={height} />
      {content}
    </svg>
  );
};
